<template>
  <section class="research-gallery-section">
    <div class="container">
      <div class="gallery-header">
        <div class="gallery-badge">
          <i class="fas fa-microscope"></i>
          <span>{{ content.header.tag }}</span>
        </div>
        <h2>{{ content.header.title }}</h2>
        <p class="gallery-subtitle">{{ content.header.subtitle }}</p>
      </div>
      
      <div class="masonry-zigzag-gallery">
        <div class="masonry-item" v-for="(item, index) in content.galleryItems" :key="index" :class="item.size">
          <div class="masonry-card">
            <img :src="item.image" :alt="item.title" class="masonry-image" />
            <div class="masonry-overlay">
              <div class="overlay-content">
                <h3>{{ item.title }}</h3>
                <p>{{ item.description }}</p>
                <div class="overlay-tags">
                  <span v-for="(tag, tagIndex) in item.tags" :key="tagIndex" class="overlay-tag">
                    {{ tag }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'ResearchItsGalleryComp',
  props: {
    content: {
      type: Object,
      required: true
    }
  }
}
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Research Gallery Section */
.research-gallery-section {
  padding: 120px 0;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  position: relative;
  overflow: hidden;
}

.research-gallery-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.03)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
  z-index: 1;
}

.gallery-header {
  text-align: center;
  margin-bottom: 80px;
  position: relative;
  z-index: 2;
}

.gallery-badge {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 24px;
  border-radius: 30px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 30px;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
}

.gallery-header h2 {
  font-size: 56px;
  font-weight: 800;
  background: linear-gradient(135deg, #ffffff 0%, #a8a8a8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 25px;
  font-family: 'Archivo', sans-serif;
  line-height: 1.2;
}

.gallery-subtitle {
  font-size: 20px;
  color: rgba(255, 255, 255, 0.7);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  font-weight: 300;
}

/* Masonry Zigzag Gallery */
.masonry-zigzag-gallery {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: 15px;
  gap: 15px;
  margin-top: 60px;
  position: relative;
  z-index: 2;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
}

.masonry-item {
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

.masonry-item:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.masonry-item.square {
  grid-row: span 18;
}

.masonry-item.wide {
  grid-column: span 2;
  grid-row: span 15;
}

.masonry-item.tall {
  grid-row: span 25;
}

.masonry-card {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 20px;
  overflow: hidden;
}

.masonry-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.masonry-item:hover .masonry-image {
  transform: scale(1.1);
}

.masonry-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%);
  color: white;
  padding: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
}

.masonry-item:hover .masonry-overlay {
  opacity: 1;
}

.overlay-content {
  text-align: center;
  transform: translateY(20px);
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.masonry-item:hover .overlay-content {
  transform: translateY(0);
}

.overlay-content h3 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
  color: white;
  line-height: 1.3;
}

.overlay-content p {
  font-size: 16px;
  margin-bottom: 20px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 300;
}

.overlay-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.overlay-tag {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.overlay-tag:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* Responsive Design for Masonry */
@media (max-width: 1200px) {
  .masonry-zigzag-gallery {
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: 14px;
    gap: 14px;
  }
  
  .masonry-item.wide {
    grid-column: span 2;
  }
  
  .masonry-item.square {
    grid-row: span 16;
  }
  
  .masonry-item.wide {
    grid-row: span 13;
  }
  
  .masonry-item.tall {
    grid-row: span 22;
  }
}

@media (max-width: 768px) {
  .masonry-zigzag-gallery {
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: 12px;
    gap: 12px;
    margin-top: 40px;
  }
  
  .masonry-item.wide {
    grid-column: span 2;
  }
  
  .masonry-item.square {
    grid-row: span 14;
  }
  
  .masonry-item.wide {
    grid-row: span 12;
  }
  
  .masonry-item.tall {
    grid-row: span 20;
  }
  
  .gallery-header h2 {
    font-size: 42px;
  }
  
  .gallery-subtitle {
    font-size: 18px;
  }
  
  .overlay-content h3 {
    font-size: 20px;
  }
  
  .overlay-content p {
    font-size: 14px;
  }
  
  .masonry-overlay {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .research-gallery-section {
    padding: 80px 0;
  }
  
  .masonry-zigzag-gallery {
    grid-template-columns: 1fr;
    grid-auto-rows: 10px;
    gap: 10px;
  }
  
  .masonry-item.wide {
    grid-column: span 1;
  }
  
  .masonry-item.square {
    grid-row: span 12;
  }
  
  .masonry-item.wide {
    grid-row: span 10;
  }
  
  .masonry-item.tall {
    grid-row: span 18;
  }
  
  .gallery-header h2 {
    font-size: 36px;
  }
  
  .masonry-overlay {
    padding: 15px;
  }
  
  .overlay-content h3 {
    font-size: 18px;
  }
  
  .overlay-content p {
    font-size: 13px;
  }
}
</style>
